import { type ReactNode, Suspense, lazy, useMemo, useSyncExternalStore } from 'react';
import { uiT } from '../i18n';

export type ToastTone = 'info' | 'ok' | 'warn' | 'err';

export type ToastAction = { label: string; onClick: () => void | Promise<void> };

export type ToastInput = {
  title: string;
  body?: string;
  tone?: ToastTone;
  /** Auto-dismiss after this many ms. Errors persist until dismissed unless set; `0` never dismisses. */
  durationMs?: number;
  /** One action button (e.g. "View"). */
  action?: ToastAction;
  /** Offer Undo: the toast stays 10 s, and choosing Undo runs this and confirms "Undone". */
  undo?: () => void | boolean | Promise<void | boolean>;
  /** A visible toast with the same key is updated in place instead of stacking a duplicate. */
  dedupeKey?: string;
};

export type ToastPromiseMessages<T> = {
  loading: string;
  success: string | ((value: T) => string);
  error?: string | ((error: unknown) => string);
};

/** Operations the lazily loaded toast host executes (queued until it is mounted). */
export type ToastOp =
  | { kind: 'add'; id: string; input: ToastInput }
  | { kind: 'dismiss'; id?: string }
  | {
      kind: 'promise';
      promise: Promise<unknown>;
      messages: ToastPromiseMessages<unknown>;
    };

type Executor = (op: ToastOp) => void;

/**
 * Bridge between `toast()` (available everywhere, tiny) and the toast host (Base UI Toast, loaded on
 * the first toast so it never weighs on first paint).
 */
const bridge = {
  executor: null as Executor | null,
  queue: [] as ToastOp[],
  wanted: false,
  listeners: new Set<() => void>(),
  seq: 0,
  run(op: ToastOp) {
    if (this.executor) this.executor(op);
    else {
      this.queue.push(op);
      if (!this.wanted) {
        this.wanted = true;
        for (const l of [...this.listeners]) l();
      }
    }
  },
};

/** Called by the host once mounted: drains the queue. */
export function connectToastHost(executor: Executor | null): void {
  bridge.executor = executor;
  if (!executor) return;
  const pending = bridge.queue.splice(0);
  for (const op of pending) executor(op);
}

function nextId(input: ToastInput): string {
  if (input.dedupeKey) return `dedupe:${input.dedupeKey}`;
  bridge.seq += 1;
  return `toast-${bridge.seq}`;
}

export type ToastFn = ((input: ToastInput) => string) & {
  /** Loading → success / error toast for a promise; resolves with the promise's value. */
  promise: <T>(promise: Promise<T>, messages: ToastPromiseMessages<T>) => Promise<T>;
  dismiss: (id?: string) => void;
};

/** Show a toast from anywhere (also outside React). Returns the toast id. */
export const toast: ToastFn = Object.assign(
  (input: ToastInput) => {
    const id = nextId(input);
    bridge.run({ kind: 'add', id, input });
    return id;
  },
  {
    promise: <T,>(promise: Promise<T>, messages: ToastPromiseMessages<T>) => {
      bridge.run({
        kind: 'promise',
        promise,
        messages: {
          ...messages,
          error: messages.error ?? uiT('ui.toast.failed'),
        } as ToastPromiseMessages<unknown>,
      });
      return promise;
    },
    dismiss: (id?: string) => bridge.run({ kind: 'dismiss', id }),
  },
);

const ToastHost = lazy(() => import('./ToastHost'));

function subscribe(listener: () => void) {
  bridge.listeners.add(listener);
  return () => bridge.listeners.delete(listener);
}

/**
 * Place once at the app root. Toasts sit in `#dr-system` above every layer (and above the mobile tab
 * bar), pause while hovered or focused, F6 moves focus to them, errors persist until dismissed. The
 * toast host loads with the first toast.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const wanted = useSyncExternalStore(
    subscribe,
    () => bridge.wanted,
    () => false,
  );
  return (
    <>
      {children}
      {wanted ? (
        <Suspense fallback={null}>
          <ToastHost />
        </Suspense>
      ) : null}
    </>
  );
}

/** Returns the app's `toast` function (`toast({ title, tone, undo })`, `toast.promise`, `toast.dismiss`). */
export function useToast(): ToastFn {
  return useMemo(() => toast, []);
}
